import type { RedirectLoginOptions } from '@auth0/auth0-spa-js'
import type { GetAuthTokenDelegate } from '@/features/shell/platform/types'
import type {
  AuthInitialScreen,
  AuthProvider,
  ProviderLogoutOptions,
  SessionInfo,
} from '@/features/iam/auth/AuthStore'
import {
  decodeTokenClaims,
  isMfaEnrolled,
} from '@/features/iam/auth/token-claims'

/**
 * Uses the token handed over by a native app shell for authentication, and
 * the fallback provider when the shell has none and for everything else.
 */
export class NativeTokenProvider implements AuthProvider {
  constructor(
    private readonly _getAuthTokenDelegate: GetAuthTokenDelegate,
    private readonly _fallback: AuthProvider,
  ) {}

  async fetchSessionInfo(): Promise<SessionInfo | null> {
    const token = await this._getAuthTokenDelegate()
      .getToken()
      .catch((err) => {
        console.error('Failed to get native auth token:', err)
        return null
      })
    if (!token) {
      return this._fallback.fetchSessionInfo()
    }

    const claims = decodeTokenClaims(token)
    return {
      token,
      authId: claims.sub,
      mfaEnabled: isMfaEnrolled(claims),
    }
  }

  handleRedirectCallback() {
    return this._fallback.handleRedirectCallback()
  }

  redirectToAuthorize(
    initialScreen: AuthInitialScreen,
    opts: RedirectLoginOptions | undefined,
  ) {
    return this._fallback.redirectToAuthorize(initialScreen, opts)
  }

  logout(opts: ProviderLogoutOptions) {
    return this._fallback.logout(opts)
  }
}
